import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from '../styles/cms.module.css';
import { uploadImage, listCountries } from '../api';

interface Country {
  _id: string;
  name: string;
  flagImageUrl: string;
  historicImageUrl: string;
}

interface EmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: {
    couponId: string;
    firstName: string;
    lastName: string;
    email: string;
    countryName: string;
    qrImageUrl: string;
    iccid: string;
    dataAmount: number;
    duration: number;
    planName: string;
  };
  onSubmit: (formData: {
    firstName: string;
    
    email: string;
    qrImageUrl: string;
    iccid: string;
    dataAmount: number;
    duration: number;
    planName: string;
    countryName: string;
    couponId: string;
  }) => void;
}

const initialFormState = {
  firstName: '',
  
  email: '',
  qrImageUrl: '',
  iccid: '',
  dataAmount: 0,
  duration: 0,
  planName: '',
  countryName: '',
  couponId: '',
  imageFile: null as File | null,
};

const EmailModal: React.FC<EmailModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [isUploading, setIsUploading] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCountries();
      if (initialData) {
        setFormData(prevData => ({
          ...prevData,
          ...initialData
        }));
      }
    }
  }, [isOpen, initialData]);

  const fetchCountries = async () => {
    setIsLoadingCountries(true);
    try {
      const response = await listCountries({ limit: 1000 });
      const filteredCountries = response.data.countries.filter(
        (item: Country) => item.flagImageUrl && item.historicImageUrl
      );
      setCountries(filteredCountries);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'file' && 'files' in e.target && e.target.files && e.target.files[0]) {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', e.target.files[0]);

      try {
        const response = await uploadImage(formData);
        setFormData(prev => ({ 
          ...prev, 
          qrImageUrl: response.data.url 
        }));
        toast.success('QR code image uploaded successfully!');
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload QR code image');
      } finally {
        setIsUploading(false);
      }
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseFloat(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.qrImageUrl) {
      toast.error('Please upload a QR code image');
      return;
    }
    const { imageFile, ...submitData } = formData;
    // Convert GB to MB for dataAmount
    submitData.dataAmount = submitData.dataAmount * 1000;
    onSubmit(submitData);
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <h2>Send eSIM Email</h2>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="firstName">Full Name*</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </div>

          {/* <div className={styles.formGroup}>
            <label htmlFor="lastName">Last Name*</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div> */}

          <div className={styles.formGroup}>
            <label htmlFor="email">Email*</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="iccid">ICCID*</label>
            <input
              type="text"
              id="iccid"
              name="iccid"
              value={formData.iccid}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="planName">Plan Name*</label>
            <input
              type="text"
              id="planName"
              name="planName"
              value={formData.planName}
              onChange={handleChange}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="dataAmount">Data Amount (GB)*</label>
            <input
              type="number"
              id="dataAmount"
              name="dataAmount"
              value={formData.dataAmount}
              onChange={handleChange}
              min="0"
              step="0.1"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="duration">Duration (days)*</label>
            <input
              type="number"
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              min="1"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="countryName">Country*</label>
            <select
              id="countryName"
              name="countryName"
              value={formData.countryName}
              onChange={handleChange}
              required
              className={styles.select}
              disabled={isLoadingCountries}
            >
              <option value="">Select a country</option>
              {countries.map((country) => (
                <option key={country._id} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
            {isLoadingCountries && <div className={styles.loadingText}>Loading countries...</div>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="image">QR Code Image*</label>
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleChange}
              disabled={isUploading}
              required
            />
            {isUploading && <div className={styles.uploadingText}>Uploading image...</div>}
            {formData.qrImageUrl && (
              <div className={styles.imagePreview}>
                <img src={formData.qrImageUrl} alt="QR Code" />
              </div>
            )}
          </div>

          <div className={styles.modalActions}>
            <button type="submit" className={styles.submitBtn} disabled={isUploading}>
              Send
            </button>
            <button type="button" onClick={handleClose} className={styles.cancelBtn}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailModal; 